
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

export interface ChargeCalculation {
    id: string;
    competence: string; // YYYY-MM
    payrollImportId?: string; // Link to payroll import

    // Base Data
    totalBase: number;
    monthlyRevenue: number; // For CPRB

    // Results
    totalInssCompany: number;
    totalRat: number;
    totalThirdParties: number;
    totalCprb: number; // Revenue * CprbRate
    totalCharges: number;

    // Parameter Snapshot
    companySettingsSnapshot: any;

    items: ChargeCalculationItem[];
    status: 'DRAFT' | 'CLOSED';
}

export interface ChargeCalculationItem {
    collaboratorId: string;
    collaboratorName: string; // Snapshot for display
    baseValue: number;
    inssCompany: number;
    rat: number;
    thirdParties: number;
    total: number;
}

const COLLECTION = 'charge_calculations';

export const chargeCalculationService = {
    getAll: async (): Promise<ChargeCalculation[]> => {
        const snap = await getDocs(collection(db, COLLECTION));
        const data = snap.docs.map(d => ({ ...(d.data() as any), id: d.id }));
        return data.sort((a, b) => b.competence.localeCompare(a.competence));
    },

    getById: async (id: string): Promise<ChargeCalculation | undefined> => {
        const ref = doc(db, COLLECTION, id);
        const snap = await getDoc(ref);
        if (!snap.exists()) return undefined;
        return { ...(snap.data() as any), id: snap.id };
    },

    create: async (data: Omit<ChargeCalculation, 'id'>): Promise<ChargeCalculation> => {
        const newRef = doc(collection(db, COLLECTION));
        // Use ID as ref id
        const payload = { ...data, id: newRef.id };
        await setDoc(newRef, payload);
        return payload;
    },

    delete: async (id: string): Promise<void> => {
        await deleteDoc(doc(db, COLLECTION, id));
    },

    update: async (id: string, data: Partial<ChargeCalculation>): Promise<ChargeCalculation> => {
        const ref = doc(db, COLLECTION, id);
        await updateDoc(ref, data);
        // Optimistic merge for return
        // In real app, might want to re-fetch if trigger changed something
        return { id, ...data } as ChargeCalculation;
    }
};
